import { constructPaymentDateKey } from '~/builders/project-dates/ProjectDatesHelper';
import { roundInterestToNearestCent } from '~/engine/processing/calculators/core/financial-math/rounding';
import { InterestCalculationEngine } from '~/engine/processing/engines/interest-engine/interest-calculator';
import { DateCalculationEngine } from '~/engine/processing/shared/date-calculations';
import type {
  DateRecordsMapByPaymentDate,
  ExistingDebtPaymentRecord,
  ExistingDebtProject,
} from '~/types';

/**
 * Helper functions for working with existing debt data
 * These functions can be used by other components to get information about existing debt
 */

// =============================================================================
// INTEREST AND PAYMENT CALCULATIONS
// =============================================================================

/**
 * @deprecated Use InterestCalculationEngine.calculateInterest() or calculateSemiAnnualInterest() instead
 * This function is kept for backward compatibility but should not be used in new code
 *
 * @param principalPayment - Principal amount for the payment
 * @param interestRate - Annual interest rate as decimal
 * @param paymentsPerYear - Number of payments per year
 * @returns Interest amount for this payment
 */
export function calculateInterestPerPayment(
  principalPayment: number,
  interestRate: number,
  paymentsPerYear: number
): number {
  // Redirect to the standardized calculation
  // For semi-annual payments (most common case)
  if (paymentsPerYear === 2) {
    return InterestCalculationEngine.calculateSemiAnnualInterest(principalPayment, interestRate);
  }

  // For other payment frequencies, approximate using days
  const daysPerPeriod = 360 / paymentsPerYear;
  return InterestCalculationEngine.calculateInterestFromDays(
    principalPayment,
    interestRate,
    daysPerPeriod
  );
}

/**
 * Estimate payments per year based on payment schedule
 *
 * @param records - Array of payment records
 * @returns Estimated number of payments per year
 */
export function estimatePaymentsPerYear(records: ExistingDebtPaymentRecord[]): number {
  if (records.length < 2) return 2; // Default assumption

  // Calculate average time between payments
  const sortedRecords = records.sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
  const firstDate = sortedRecords[0].paymentDate;
  const lastDate = sortedRecords[sortedRecords.length - 1].paymentDate;

  const yearSpan = DateCalculationEngine.yearsBetween(firstDate, lastDate, false);
  return Math.round((records.length - 1) / yearSpan) || 2;
}

/**
 * Calculate debt payment totals including interest
 * Rounds to nearest cent to ensure precision
 *
 * @param principalPayment - Principal payment amount
 * @param principalInterest - Interest for this principal
 * @param totalInterestFromFuture - Cumulative interest from future payments
 * @returns Object with total payment interest and total payment (both rounded to cents)
 */
export function calculateDebtPaymentTotals(
  principalPayment: number,
  principalInterest: number,
  totalInterestFromFuture: number
): { totalPaymentInterest: number; totalPayment: number } {
  // Round the sum once to avoid accumulation errors
  const totalPaymentInterest = roundInterestToNearestCent(
    principalInterest + totalInterestFromFuture
  );
  const totalPayment = principalPayment + totalPaymentInterest;
  return { totalPaymentInterest, totalPayment };
}

// =============================================================================
// EXISTING DEBT QUERIES AND AGGREGATION
// =============================================================================

/**
 * Gets the total existing debt payment amount for a specific tax year
 * Only includes payments that occur within the specified tax year
 *
 * @param existingDebtPaymentRecords - Array of existing debt payment records
 * @param dateRecordsMapByPaymentDate - Map of payment dates to their tax year information
 * @param taxYear - The tax year to get existing debt for
 * @returns Total existing debt payment amount for the specified tax year
 */
export function getExistingDebtForYear(
  existingDebtPaymentRecords: ExistingDebtPaymentRecord[] | undefined,
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate | undefined,
  taxYear: number
): number {
  if (!existingDebtPaymentRecords || !dateRecordsMapByPaymentDate) {
    return 0;
  }
  return existingDebtPaymentRecords.reduce((sum, record) => {
    const paymentDateKey = constructPaymentDateKey(record.paymentDate);
    const dateRecord = dateRecordsMapByPaymentDate[paymentDateKey];

    // Only include this payment if it belongs to the specified tax year
    if (dateRecord && dateRecord.taxYear === taxYear) {
      return sum + (record.totalPayment || 0);
    }
    return sum;
  }, 0);
}

/**
 * Groups existing debt payments by tax year
 *
 * @param existingDebtPaymentRecords - Array of existing debt payment records
 * @param dateRecordsMapByPaymentDate - Map of payment dates to their tax year information
 * @returns Map of tax year to total existing debt payment amount
 */
export function getExistingDebtByYear(
  existingDebtPaymentRecords: ExistingDebtPaymentRecord[] | undefined,
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate | undefined
): Record<number, number> {
  const debtByYear: Record<number, number> = {};

  if (!existingDebtPaymentRecords || !dateRecordsMapByPaymentDate) {
    return debtByYear;
  }

  existingDebtPaymentRecords.forEach((record) => {
    const paymentDateKey = constructPaymentDateKey(record.paymentDate);
    const dateRecord = dateRecordsMapByPaymentDate[paymentDateKey];

    if (dateRecord) {
      const taxYear = dateRecord.taxYear;
      debtByYear[taxYear] = (debtByYear[taxYear] || 0) + (record.totalPayment || 0);
    }
  });

  return debtByYear;
}

/**
 * Calculates the total existing debt across all years
 *
 * @param existingDebtPaymentRecords - Array of existing debt payment records
 * @returns Total existing debt amount
 */
export function getTotalExistingDebt(
  existingDebtPaymentRecords: ExistingDebtPaymentRecord[] | undefined
): number {
  if (!existingDebtPaymentRecords) {
    return 0;
  }

  return existingDebtPaymentRecords.reduce((sum, record) => {
    return sum + (record.totalPayment || 0);
  }, 0);
}

/**
 * Gets existing debt payments for a specific fiscal year
 *
 * @param existingDebtPaymentRecords - Array of existing debt payment records
 * @param fiscalYearEndDate - The fiscal year end date to filter by
 * @returns Array of payment records for the specified fiscal year
 */
export function getExistingDebtForFiscalYear(
  existingDebtPaymentRecords: ExistingDebtPaymentRecord[] | undefined,
  fiscalYearEndDate: string
): ExistingDebtPaymentRecord[] {
  if (!existingDebtPaymentRecords) {
    return [];
  }

  return existingDebtPaymentRecords.filter((record) => {
    return record.fiscalYearInformation?.fiscalYearEndingDate === fiscalYearEndDate;
  });
}

/**
 * Calculates fiscal year totals for existing debt
 *
 * @param existingDebtPaymentRecords - Array of existing debt payment records
 * @returns Map of fiscal year end date to total payment amount
 */
export function getFiscalYearTotals(
  existingDebtPaymentRecords: ExistingDebtPaymentRecord[] | undefined
): Record<string, number> {
  const fiscalYearTotals: Record<string, number> = {};

  if (!existingDebtPaymentRecords) {
    return fiscalYearTotals;
  }

  existingDebtPaymentRecords.forEach((record) => {
    const fiscalYearEndDate = record.fiscalYearInformation?.fiscalYearEndingDate;
    if (fiscalYearEndDate) {
      fiscalYearTotals[fiscalYearEndDate] =
        (fiscalYearTotals[fiscalYearEndDate] || 0) + (record.totalPayment || 0);
    }
  });

  return fiscalYearTotals;
}

// =============================================================================
// PROJECT UTILITIES AND VALIDATION
// =============================================================================

/**
 * Calculate summary statistics for existing debt projects
 *
 * @param projects - Array of existing debt projects
 * @returns Summary object with totals and statistics
 */
export function calculateExistingDebtSummary(projects: ExistingDebtProject[]) {
  if (projects.length === 0) return null;

  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalPayments = 0;
  let paymentCount = 0;

  projects.forEach((project) => {
    project.existingDebtPaymentRecords.forEach((record) => {
      totalPrincipal += record.principalPayment;
      totalInterest += record.totalPaymentInterest;
      totalPayments += record.totalPayment;
      paymentCount++;
    });
  });

  return {
    projectCount: projects.length,
    paymentCount,
    totalPrincipal,
    totalInterest,
    totalPayments,
    averagePaymentAmount: paymentCount > 0 ? totalPayments / paymentCount : 0,
    interestRatio: totalPayments > 0 ? totalInterest / totalPayments : 0,
  };
}

/**
 * Check if existing debt projects are valid for processing
 *
 * @param projects - Array of existing debt projects
 * @returns Validation result with errors if any
 */
export function validateExistingDebtProjects(projects: ExistingDebtProject[]): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  projects.forEach((project, index) => {
    if (!project.id || project.id.trim() === '') {
      errors.push(`Project ${index + 1}: ID is required`);
    }

    if (!project.name || project.name.trim() === '') {
      errors.push(`Project ${index + 1}: Name is required`);
    }

    if (!project.existingDebtPaymentRecords || project.existingDebtPaymentRecords.length === 0) {
      errors.push(`Project ${index + 1}: At least one payment record is required`);
    } else {
      project.existingDebtPaymentRecords.forEach((record, recordIndex) => {
        if (record.principalPayment <= 0) {
          errors.push(
            `Project ${index + 1}, Payment ${recordIndex + 1}: Principal payment must be greater than 0`
          );
        }

        if (record.interestRate < 0) {
          errors.push(
            `Project ${index + 1}, Payment ${recordIndex + 1}: Interest rate cannot be negative`
          );
        }
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}
